/* eslint-disable @typescript-eslint/camelcase */

import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Status } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import useQueryParameters from 'hooks/useQueryParameters';

import {
  CreditApplication,
  mergeApplication,
  lockFields,
  selectCreditApplication,
} from 'state/creditApplication';
import { selectTenant } from 'state/tenant';
import { Rewards } from 'state/instantCredit';
import { keys, pick, pickBy, isNil, complement } from 'ramda';
import useIovation from 'hooks/useIovation';
import isPresent from 'utils/isPresent';
import useAnalyticVariables from 'hooks/useAnalyticVariables';
import { ConsumerSearch, mergeConsumerSearch } from 'state/consumerSearch';
import useHandleExistingAccount from './handleExistingAccount';
const PATH = '/api/consumer_search';

export interface SearchParams {
  mobilePhone?: string;
  token?: string;
  carrier?: string;
  keyword?: string;
  shortCode?: string;
  ssn?: string;
  zip?: string;
  otpCode?: string;
}

export interface DspPushSearchParams extends SearchParams {
  token: string;
}

interface SearchResults {
  token: string | undefined;
  accountNumber: string | undefined;
  expirationDate: string | undefined;
  expirationType: string | undefined;
  expirationValue: number | undefined;
  securityCode: string | undefined;
  firstPurchaseLimit: number | undefined;
  creditLimit: number | undefined;
  cardType: string | undefined;
  authAllowed: number | undefined;
  hasExistingAccount: boolean | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  middleInitial: string | undefined;
  address1: string | undefined;
  address2: string | undefined;
  city: string | undefined;
  state: string | undefined;
  zip: string | undefined;
  ssn: string | undefined;
  phone: string | undefined;
  returnCode: string | undefined;
  virtualCardAccountNumber: string | undefined;
  rewards?: Rewards;
}

type ConsumerSearchHook = () => [
  <T extends SearchParams = SearchParams>(params: T) => Promise<SearchResults>,
  Status,
  boolean,
];

export class ConsumerSearchError extends Error {}

const prefillValues = (results: SearchResults): Partial<CreditApplication> =>
  pickBy(complement(isNil), {
    firstName: results.firstName,
    lastName: results.lastName,
    address1: results.address1,
    address2: results.address2,
    city: results.city,
    state: results.state,
    zip: results.zip,
    ssn: results.ssn,
    mobilePhone: results.phone,
  });

/**
 * Copy existing values
 */
const overrideSearchResults = (
  searchResults: SearchResults,
  creditApplication: CreditApplication,
): SearchResults => {
  const searchResultsKeys = (keys(
    searchResults,
  ) as unknown) as (keyof SearchResults)[];

  const overrideValue = {
    ...creditApplication,
    phone: creditApplication.mobilePhone,
  };

  const overrides = pickBy(isPresent, overrideValue) as Partial<
    typeof overrideValue
  >;

  return pick(searchResultsKeys, {
    ...searchResults,
    ...overrides,
  }) as SearchResults;
};

const useConsumerSearch: ConsumerSearchHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const dispatch = useDispatch();
  const { mockCS, location, channel, subchannel } = useQueryParameters();
  const iovationBlackbox = useIovation();
  const creditApplication = useSelector(selectCreditApplication);
  const tenant = useSelector(selectTenant);
  const { setAnalyticsVariables } = useAnalyticVariables();

  const handleExistingAccount = useHandleExistingAccount();

  const searchConsumer = useCallback(
    async <T extends SearchParams = SearchParams>({
      carrier,
      keyword,
      mobilePhone,
      shortCode,
      ssn,
      token,
      zip,
      otpCode,
    }: T): Promise<SearchResults> => {
      setError(false);

      const blackbox = await iovationBlackbox;

      const response = await fetch(PATH, {
        method: 'POST',
        body: {
          carrier: carrier,
          keyword: keyword,
          mobilePhone,
          shortCode,
          ssn: ssn,
          token: token,
          zip: zip,
          iovationBlackbox: blackbox,
          mockResponse: mockCS,
          location,
          channel,
          subchannel,
          ...(otpCode ? { otp_code: otpCode } : {}),
        },
      });

      if (!response.ok) {
        setError(true);
        dispatch(mergeConsumerSearch({ result: 'error' }));
        return Promise.reject(new ConsumerSearchError());
      }

      // TODO: Currently just shoving all of the data into place. Should probably
      // be more discerning somehow. Revisit this.
      let searchResults: SearchResults = await response.json();

      if (tenant.trustedBrandPrefill) {
        searchResults = overrideSearchResults(searchResults, creditApplication);
      }
      const consumerSearchResult: ConsumerSearch = searchResults.hasExistingAccount
        ? { result: 'existingAccount' }
        : { result: 'success' };

      dispatch(mergeConsumerSearch(consumerSearchResult));
      dispatch(mergeApplication(prefillValues(searchResults)));
      dispatch(
        lockFields({
          ssn: Boolean(searchResults.ssn),
          mobilePhone: Boolean(mobilePhone || searchResults.phone),
        }),
      );

      if (searchResults.hasExistingAccount) {
        handleExistingAccount(
          searchResults,
          {
            sendToSdk: true,
            isValidated: true,
          },
          { mobilePhone: mobilePhone || null },
        );
      }

      setAnalyticsVariables({ consumerSearchSuccessful: 'true' });
      return searchResults;
    },
    [
      iovationBlackbox,
      fetch,
      mockCS,
      location,
      channel,
      subchannel,
      tenant.trustedBrandPrefill,
      dispatch,
      setAnalyticsVariables,
      creditApplication,
      handleExistingAccount,
    ],
  );

  return [searchConsumer, status, error];
};

export default useConsumerSearch;
